import { defineSlotRecipe } from '@chakra-ui/react';

/*
 * Design system source: .claude/skills/monetizenow-design/preview/menu-popover.html
 *
 * List: 8px radius, 1px border-default, shadow.md, 4px padding, bg surface.
 * Items: 7px/10px padding, 5px radius, hover bg neutral-50 (bg.subtle).
 * Group label: 10px semibold uppercase muted.
 *
 * v3 port notes:
 * - Slots renamed from v2's `menuAnatomy`: `button` → `trigger`, `list` →
 *   `content`, `groupTitle` → `itemGroupLabel`, `command` → `itemCommand`,
 *   `divider` → `separator`. `icon` is dropped (no consumer used it).
 * - Chakra's built-in `menu` recipe deep-merges *underneath* this one, same
 *   mechanism as Card.ts/Tabs.ts — confirmed empirically via a throwaway
 *   `createSystem`, not by reading the built-in's source alone:
 *   1. This app's `outline`/`action` variant names don't collide with the
 *      built-in's own variant names (`subtle`/`solid`), so those merge in
 *      cleanly as new options with no bleed-through to guard against.
 *   2. The built-in's `size` variant (`defaultVariants: { size: 'md' }`,
 *      applied even though this recipe never sets a `size` prop anywhere)
 *      DOES bleed through and wins over this file's `base` styles — variant
 *      styles cascade after base regardless of custom/built-in origin. Its
 *      `md` size overrides `content`'s `padding`/`minWidth` and `item`'s
 *      `paddingInline`/`paddingBlock` with its own values, silently
 *      shrinking the list width and item padding below the design spec.
 *      Fixed by defining this recipe's own `size.sm`/`size.md` — identical
 *      to each other and to `base` — so the resolved style is size-invariant
 *      and always matches the spec, regardless of which size value (if any)
 *      a future caller passes.
 * - `MenuItem`'s `value` is a new required prop in v3 (no v2 equivalent) —
 *   every real call site sweept in this track was auditted for accidental
 *   duplicate literal values (e.g. multiple items all passed `value="item-0"`
 *   by an earlier partial codemod pass), since a duplicate `value` collides
 *   on the DOM id Ark derives from it and breaks typeahead/highlight
 *   tracking across those items.
 */

const contentBase = {
  borderRadius: 'lg', // 8px
  bg: 'bg.surface',
  border: '1px solid',
  borderColor: 'border.subtle', // lighter than default; pairs better with shadow.md
  boxShadow: 'md',
  // 6px padding (vs 4px) so item hover/focus bg has clearance from the
  // content border on the right — the bg was visually clipping at the edge.
  padding: '6px',
  scrollPadding: '6px',
  minWidth: '200px',
  marginTop: '-4px', // Chakra's default popper gutter is ~8px; trim to ~4px total
  zIndex: 1500,
  fontSize: 'sm', // 13px — anchor cascade so nested content stays on scale
  color: 'fg.primary',
};

const itemBase = {
  color: 'fg.primary',
  fontSize: 'sm', // 13px
  borderRadius: '5px',
  paddingInline: '10px',
  paddingBlock: '7px',
  gap: 0,
  // The built-in item uses the `menuitem` cursor token, which v3 maps to
  // `default`; v2 menu items showed a pointer.
  cursor: 'pointer',
  _disabled: { cursor: 'not-allowed' },
  _hover: {
    bg: 'bg.subtle',
    color: 'fg.primary',
  },
  _focus: {
    bg: 'bg.subtle',
    color: 'fg.primary',
  },
  // Arrow-key navigation never focuses the item itself: DOM focus stays on
  // the content (aria-activedescendant) and the active item only gets
  // `data-highlighted`, so without this keyboard users saw no highlight.
  _highlighted: {
    bg: 'bg.subtle',
    color: 'fg.primary',
  },
  // v2's `MenuItemOption` kept a check-sized gap before every label, checked
  // or not (icon at 0.8em + 0.75rem margin). The v3 indicator is absolutely
  // positioned in that gap (see `itemIndicator`), so radio/checkbox items
  // reserve it as start padding and labels line up either way.
  '&[data-type=radio], &[data-type=checkbox]': {
    paddingInlineStart: 'calc(10px + 0.8em + 0.75rem)',
  },
};

export const menuTheme = defineSlotRecipe({
  slots: [
    'trigger',
    'content',
    'item',
    'itemGroupLabel',
    'itemIndicator',
    'itemCommand',
    'separator',
  ],
  base: {
    trigger: {
      fontWeight: 'normal',
      color: 'fg.primary',
      _hover: {
        bg: 'bg.hover',
        color: 'fg.primary',
      },
      _active: { bg: 'none' },
    },
    content: contentBase,
    item: itemBase,
    itemGroupLabel: {
      color: 'fg.muted',
      fontSize: '10px',
      fontWeight: 600,
      letterSpacing: 'wider',
      textTransform: 'uppercase',
      mx: 2,
      mt: 2,
      mb: 1,
    },
    // v2's option check: 0.8em at the item's start padding. The built-in
    // already centres it absolutely; this moves it from 8px to the 10px
    // padding and sizes the default check icon to v2's 1em of that 0.8em.
    itemIndicator: {
      insetStart: '10px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '0.8em',
      '& svg': { boxSize: '1em' },
    },
    itemCommand: {
      fontFamily:
        'var(--mn-font-mono, "JetBrains Mono", "SF Mono", Menlo, Consolas, monospace)',
      fontSize: '10px',
      color: 'fg.muted',
      fontWeight: 500,
    },
    separator: {
      borderColor: 'border.subtle',
      my: 1,
    },
  },
  variants: {
    variant: {
      outline: {
        trigger: {
          height: 8,
          color: 'fg.primary',
          _hover: {
            bg: 'bg.hover',
            color: 'fg.primary',
          },
          '& > span': {
            display: 'flex',
            alignItems: 'center',
          },
        },
      },
      action: {
        trigger: {
          height: 6,
          minWidth: 4,
          padding: 0,
          '& > span': {
            display: 'flex',
            alignItems: 'center',
          },
        },
      },
    },
    // Neutralizes the built-in `menu` recipe's own `size` variant (default
    // `md`) — see header comment. Both options are intentionally identical
    // to `base` and to each other; this app never varies Menu by size.
    size: {
      sm: { content: contentBase, item: itemBase },
      md: { content: contentBase, item: itemBase },
    },
  },
  defaultVariants: {
    variant: 'outline',
    size: 'md',
  },
});

export default menuTheme;
